import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import endOfYear from 'date-fns/endOfYear';
import { CookieService } from 'ngx-cookie-service';

import { WindowRefService } from '@main-client/src/app/window-ref.service';

import { environment } from '@libs/src/environments/environment';
import { isSupportedLocale } from '@libs/src/locale/locale.helper';
import { SupportedLocaleCodes } from '@libs/src/locale/locale.interface';
import { Account } from '@libs/src/models/account.model';
import { Tenant } from '@libs/src/tenant/tenant.interface';

import attempt from 'lodash-es/attempt';
import defer from 'lodash-es/defer';
import isError from 'lodash-es/isError';

@Injectable({ providedIn: 'root' })
export class LocaleSettingsService {
  readonly localeCookieKey = 'ttc_locale_settings';
  readonly toughCookieParserJsonPrefix = 'j:';

  constructor(
    @Inject(LOCALE_ID) public appLocale: string,
    public cookieService: CookieService,
    public windowService: WindowRefService,
  ) {}

  getLocaleCookie(): { locale_selected: string } {
    const value = attempt(() => {
      const cookieValue = this.cookieService.get(this.localeCookieKey);
      if (!cookieValue) {
        return;
      }
      const sanitizedCookieValue = cookieValue.replace(
        this.toughCookieParserJsonPrefix,
        '',
      );
      return JSON.parse(sanitizedCookieValue);
    });
    return isError(value) ? '' : value;
  }

  setLocaleCookie(locale: string) {
    this.cookieService.set(
      this.localeCookieKey,
      this.generateLocaleCookieValue(locale),
      { expires: new Date(endOfYear(new Date('2099'))), path: '/' },
    );
  }

  deleteLocaleCookie() {
    this.cookieService.delete(this.localeCookieKey);
    this.cookieService.delete(this.localeCookieKey, '/');
  }

  generateLocaleCookieValue(localeSelected: string) {
    const value = this.generateLocaleConfig(localeSelected);
    return `${this.toughCookieParserJsonPrefix}${JSON.stringify(value)}`;
  }

  generateLocaleConfig(localeSelected: string) {
    return {
      locale_selected: localeSelected,
      last_modified_date: new Date(Date.now()),
    };
  }

  ensureBrowserLocale(
    tenant: Tenant,
    userLocale: typeof SupportedLocaleCodes[number],
  ) {
    if (!environment.production) {
      return;
    }
    const localeCookieSelected = this.getLocaleCookie()?.locale_selected;
    const userLocaleSelected = userLocale;
    const isLocalizationEnabled = tenant.enabled_features?.localization;
    if (!isLocalizationEnabled && !!localeCookieSelected) {
      this.deleteLocaleCookie();
      return;
    }
    if (
      isLocalizationEnabled &&
      userLocaleSelected &&
      isSupportedLocale(userLocaleSelected) &&
      userLocaleSelected !== this.appLocale
    ) {
      this.deleteLocaleCookie();
      this.setLocaleCookie(userLocaleSelected);
      defer(() => this.windowService.nativeWindow.location.reload());
    }
  }
}
